"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBinaryBytecodeDecoderTemplate = getBinaryBytecodeDecoderTemplate;
exports.getBinaryBytecodeAccessTemplate = getBinaryBytecodeAccessTemplate;
function getBinaryBytecodeDecoderTemplate() {
    return `
            // Binary bytecode decoder
            // Constant type tags
            let CT_NULL = 0, CT_UNDEF = 1, CT_FALSE = 2, CT_TRUE = 3;
            let CT_INT8 = 4, CT_INT16 = 5, CT_INT32 = 6, CT_FLOAT64 = 7;
            let CT_STRING = 8, CT_BIGINT = 9, CT_REGEX = 10;

            // Flag bits
            let FB_ARROW = 1, FB_ASYNC = 2, FB_GEN = 4, FB_FNAME = 8;
            let FB_OSEED = 16, FB_OMAP = 32, FB_JKEY = 64, FB_BKEY = 128;
            let FB_SMSEED = 256, FB_SMSTATE = 512, FB_SEKEY = 1024;
            let FB_JUMPS = 2048, FB_EXCEPT = 4096;

            // Binary reader class
            function BinaryReader(buf) {
                this.buf = buf;
                // IMPORTANT: Must pass byteOffset and byteLength to handle sliced buffers correctly
                this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
                this.off = 0;
            }
            BinaryReader.prototype.u8 = function() { return this.buf[this.off++]; };
            BinaryReader.prototype.u16 = function() {
                let v = this.view.getUint16(this.off, true);
                this.off += 2;
                return v;
            };
            BinaryReader.prototype.u32 = function() {
                let v = this.view.getUint32(this.off, true);
                this.off += 4;
                return v;
            };
            BinaryReader.prototype.i32 = function() {
                let v = this.view.getInt32(this.off, true);
                this.off += 4;
                return v;
            };
            BinaryReader.prototype.f64 = function() {
                let v = this.view.getFloat64(this.off, true);
                this.off += 8;
                return v;
            };
            BinaryReader.prototype.varint = function() {
                let result = 0, shift = 0, b;
                do {
                    b = this.u8();
                    result |= (b & 0x7F) << shift;
                    shift += 7;
                } while (b >= 0x80);
                // Zigzag decode
                return (result >>> 1) ^ -(result & 1);
            };
            BinaryReader.prototype.str = function() {
                let len = this.varint();
                let bytes = this.buf.slice(this.off, this.off + len);
                this.off += len;
                return new TextDecoder().decode(bytes);
            };

            // Decode base64 to Uint8Array
            function b64ToUint8(b64) {
                if (typeof Buffer !== 'undefined') {
                    return new Uint8Array(Buffer.from(b64, 'base64'));
                }
                let bin = atob(b64);
                let arr = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) {
                    arr[i] = bin.charCodeAt(i);
                }
                return arr;
            }

            // Read a constant value
            function readConst(r) {
                let type = r.u8();
                switch (type) {
                    case CT_NULL: return null;
                    case CT_UNDEF: return undefined;
                    case CT_FALSE: return false;
                    case CT_TRUE: return true;
                    case CT_INT8: {
                        let v = r.u8();
                        return v > 127 ? v - 256 : v;
                    }
                    case CT_INT16: {
                        let v = r.u16();
                        return v > 32767 ? v - 65536 : v;
                    }
                    case CT_INT32: return r.i32();
                    case CT_FLOAT64: return r.f64();
                    case CT_STRING: return r.str();
                    case CT_BIGINT: return BigInt(r.str());
                    case CT_REGEX: {
                        let src = r.str();
                        let flags = r.str();
                        return new RegExp(src, flags);
                    }
                    default: return null;
                }
            }

            // Decode binary bytecode
            function _decodeBinary(b64, keyMap) {
                let buf = b64ToUint8(b64);
                let r = new BinaryReader(buf);

                // Version
                let version = r.u8();

                // Flags
                let flags = r.u16();

                // Basic fields
                let params = r.varint();
                let locals = r.varint();

                // Key names (use mapping if provided)
                let kI = keyMap ? keyMap.i : 'i';
                let kC = keyMap ? keyMap.c : 'c';
                let kP = keyMap ? keyMap.p : 'p';
                let kL = keyMap ? keyMap.l : 'l';
                let kJ = keyMap ? keyMap.j : 'j';
                let kX = keyMap ? keyMap.x : 'x';
                let kA = keyMap ? keyMap.a : 'a';
                let kS = keyMap ? keyMap.s : 's';
                let kG = keyMap ? keyMap.g : 'g';
                let kNi = keyMap ? keyMap.ni : 'ni';
                let kOs = keyMap ? keyMap.os : 'os';
                let kO = keyMap ? keyMap.o : 'o';
                let kJk = keyMap ? keyMap.jk : 'jk';
                let kBk = keyMap ? keyMap.bk : 'bk';
                let kSmSeed = keyMap ? keyMap.smSeed : 'smSeed';
                let kSmState = keyMap ? keyMap.smState : 'smState';
                let kSeKey = keyMap ? keyMap.seKey : 'seKey';

                let result = {};
                result[kP] = params;
                result[kL] = locals;

                // Optional fields based on flags
                if (flags & FB_FNAME) result[kNi] = r.varint();
                if (flags & FB_OSEED) result[kOs] = r.u32();
                if (flags & FB_OMAP) {
                    let mapLen = r.varint();
                    let omap = {};
                    for (let i = 0; i < mapLen; i++) {
                        let logical = r.varint();
                        let randomized = r.varint();
                        omap[logical] = randomized;
                    }
                    result[kO] = omap;
                }
                if (flags & FB_JKEY) result[kJk] = r.u32();
                if (flags & FB_BKEY) result[kBk] = r.u32();
                if (flags & FB_SMSEED) result[kSmSeed] = r.u32();
                if (flags & FB_SMSTATE) result[kSmState] = r.varint();
                if (flags & FB_SEKEY) result[kSeKey] = r.u32();
                if (flags & FB_ARROW) result[kA] = 1;
                if (flags & FB_ASYNC) result[kS] = 1;
                if (flags & FB_GEN) result[kG] = 1;

                // Constants
                let constLen = r.varint();
                let constants = [];
                for (let i = 0; i < constLen; i++) {
                    constants.push(readConst(r));
                }
                result[kC] = constants;

                // Instructions (flat array)
                let instrLen = r.varint();
                let instructions = [];
                for (let i = 0; i < instrLen; i++) {
                    let op = r.varint();
                    let argType = r.u8();
                    let arg;
                    switch (argType) {
                        case CT_NULL: arg = null; break;
                        case CT_INT8: {
                            let v = r.u8();
                            arg = v > 127 ? v - 256 : v;
                            break;
                        }
                        case CT_INT16: {
                            let v = r.u16();
                            arg = v > 32767 ? v - 65536 : v;
                            break;
                        }
                        case CT_INT32: arg = r.i32(); break;
                        case CT_FLOAT64: arg = r.f64(); break;
                        case CT_STRING: arg = r.str(); break;
                        default: arg = null;
                    }
                    instructions.push(op);
                    instructions.push(arg);
                }
                result[kI] = instructions;

                // Jumps
                if (flags & FB_JUMPS) {
                    let jumpLen = r.varint();
                    let jumps = {};
                    for (let i = 0; i < jumpLen; i++) {
                        let idx = r.varint();
                        let target = r.varint();
                        jumps[idx] = target;
                    }
                    result[kJ] = jumps;
                }

                // Exceptions
                if (flags & FB_EXCEPT) {
                    let excLen = r.varint();
                    let exceptions = {};
                    for (let i = 0; i < excLen; i++) {
                        let idx = r.varint();
                        let catchIdx = r.varint() - 1; // -1 to restore -1 case
                        let finallyIdx = r.varint() - 1;
                        let endIdx = r.varint() - 1;
                        exceptions[idx] = [catchIdx, finallyIdx, endIdx];
                    }
                    result[kX] = exceptions;
                }

                return result;
            }
`;
}
function getBinaryBytecodeAccessTemplate() {
    return `
            // Bytecode access with binary decoding
            let _bcCache = {};
            function _getBytecode(id) {
                if (_bcCache[id]) return _bcCache[id];
                let raw = {vmBytecodeArrayName}[id];
                if (typeof raw === 'string') {
                    // Binary format - decode from base64
                    _bcCache[id] = _decodeBinary(raw, {keyMappingRef});
                } else {
                    // Already an object (legacy JSON format)
                    _bcCache[id] = raw;
                }
                return _bcCache[id];
            }
`;
}
